// Hörerseite eines Kanals (f-web): eigene Seite einschalten, Aussehen, Rückweg
// zum Radio und eigene Adresse.
//
// Links die Einstellungen, rechts eine Miniatur, die sofort zeigt, wie Name,
// Logo und Farben zusammenspielen – wie bei „Hörerseite & Farben“ des Radios.
// Leere Felder erben vom Radio; die Vorschau zeigt dann die Radiowerte.
// Die Seiten selbst baut man in „Seiten bauen“ (Bereich = dieser Kanal).

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, kanalSeiteApi, type KanalRechte, type KanalSeite, type SitePageSummary } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { ImageField } from "../components/block-fields";
import { errorText } from "../components/streaming-ui";
import { Meldungen, Schalter, Sym } from "../components/social-ui";
import { Badge, Callout, Card, Page, PageHeader, Skeleton } from "../components/ui";
import type { BrandingJson, ChannelWithEndpoints } from "../types";
import { KanalReiter } from "./ChannelsPage";
import { useMedienKanal } from "../components/media-picker";

type FarbSchluessel = "primary" | "secondary" | "accent" | "background" | "text";

const FARBEN: Array<{ key: FarbSchluessel; label: string; hint: string }> = [
  { key: "primary", label: "Primärfarbe", hint: "Knöpfe, Player, Hervorhebungen" },
  { key: "secondary", label: "Sekundärfarbe", hint: "Kopfbereich" },
  { key: "accent", label: "Akzentfarbe", hint: "„Live“, kleine Hingucker" },
  { key: "background", label: "Hintergrund", hint: "Seitenhintergrund" },
  { key: "text", label: "Schrift", hint: "Fließtext" },
];

/** Vorgaben, wenn weder Kanal noch Radio etwas sagen – dieselben wie bei BrandingPage. */
const GRUND: Record<FarbSchluessel, string> = {
  primary: "#ff5500",
  secondary: "#222222",
  accent: "#ffcc00",
  background: "#ffffff",
  text: "#1f2937",
};

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * Basisadresse der Hörerseite und Adresse der Kanalseite. Die API liefert
 * `public_url`; ob das schon die volle Kanaladresse ist oder nur die Basis,
 * fangen wir beides ab, damit Logo-Vorschau und Link stimmen.
 */
export function kanalAdressen(publicUrl: string, radio: string, kanal: string): { basis: string; seite: string } {
  const url = publicUrl.replace(/\/+$/, "");
  const marke = `/${radio}/sender/`;
  if (url.includes(marke)) return { basis: url.slice(0, url.indexOf(marke)), seite: url };
  return { basis: url, seite: `${url}/${radio}/sender/${kanal}` };
}

/**
 * Eingetippte Adresse → Hostname. Wer „https://www.beispiel.de/“ einfügt,
 * meint „www.beispiel.de“; Port und Pfad gehören nicht dazu.
 */
export function hostAus(eingabe: string): string {
  return eingabe
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.$/, "");
}

const HOST = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

const LEER: KanalSeite = {
  aktiv: false,
  titel: null,
  untertitel: null,
  logo_url: null,
  farben: {},
  settings: {},
  zurueck_link: true,
  eigene_adresse: null,
  updated_at: null,
};

function fehlerText(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (raw.includes("host_taken")) return "Diese Adresse nutzt schon ein anderer Kanal oder ein anderes Radio.";
  if (raw.includes("host_invalid")) return "Diese Adresse geht nicht. Gemeint ist ein Name wie „musik.beispiel.de“ – ohne https:// und ohne Pfad.";
  // Die API schickt meist schon einen deutschen Satz mit – den nehmen wir.
  try {
    const body = JSON.parse(raw.replace(/^\d{3}\s*/, "")) as { message?: string };
    if (body.message) return body.message;
  } catch {
    /* kein JSON */
  }
  if (raw.startsWith("403")) return "Dafür fehlen die Rechte.";
  return errorText(err);
}

export function KanalHoererseitePage({ slug }: { slug: string }): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [kanal, setKanal] = useState<ChannelWithEndpoints | null>(null);
  useMedienKanal(kanal?.id ?? null);
  const [radio, setRadio] = useState<BrandingJson>({});
  const [form, setForm] = useState<KanalSeite>(LEER);
  const [seiten, setSeiten] = useState<SitePageSummary[]>([]);
  const [publicUrl, setPublicUrl] = useState("");
  const [rechte, setRechte] = useState<KanalRechte | null>(null);
  const [adresse, setAdresse] = useState("");
  const [geladen, setGeladen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const [ch, site, br] = await Promise.all([
        api.getChannel(slug),
        kanalSeiteApi.get(slug),
        api.getBranding().catch(() => ({ branding: {} as BrandingJson })),
      ]);
      setKanal(ch);
      setForm({ ...LEER, ...site.site, farben: { ...(site.site.farben ?? {}) } });
      setAdresse(site.site.eigene_adresse ?? "");
      setSeiten(site.pages ?? []);
      setPublicUrl(site.public_url);
      setRadio(br.branding ?? {});
      // Fehlt die Angabe (ältere API), gilt die Radiorolle als Näherung.
      setRechte(site.rechte ?? { pflegen: isAdmin, verwalten: isAdmin });
      setDirty(false);
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setGeladen(true);
    }
  }, [slug, isAdmin]);

  useEffect(() => { void laden(); }, [laden]);

  const set = (patch: Partial<KanalSeite>): void => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const setFarbe = (key: FarbSchluessel, wert: string | null): void => {
    setForm((f) => {
      const farben = { ...f.farben };
      if (wert === null || wert === "") delete farben[key];
      else farben[key] = wert;
      return { ...f, farben };
    });
    setDirty(true);
  };

  const radioSlug = user?.tenant_slug ?? "";
  const { basis, seite } = kanalAdressen(publicUrl, radioSlug, slug);
  const darfPflegen = rechte?.pflegen ?? false;
  const darfVerwalten = rechte?.verwalten ?? false;

  const speichern = async (e?: FormEvent, ueberschreibe?: Partial<KanalSeite>): Promise<void> => {
    e?.preventDefault();
    const host = hostAus(adresse);
    if (host !== "" && !HOST.test(host)) {
      setError("Diese Adresse geht nicht. Gemeint ist ein Name wie „musik.beispiel.de“ – ohne https:// und ohne Pfad.");
      return;
    }
    // Ungültige Farbwerte nicht mitschicken – die Hörerseite bekäme sonst Unsinn ins CSS.
    const farben = Object.fromEntries(Object.entries(form.farben).filter(([, v]) => typeof v === "string" && HEX.test(v)));
    const leerZuNull = (v: string | null): string | null => (v && v.trim() !== "" ? v.trim() : null);
    const rumpf: Omit<KanalSeite, "updated_at"> = {
      aktiv: form.aktiv,
      titel: leerZuNull(form.titel),
      untertitel: leerZuNull(form.untertitel),
      logo_url: leerZuNull(form.logo_url),
      farben,
      settings: form.settings ?? {},
      zurueck_link: form.zurueck_link,
      eigene_adresse: host === "" ? null : host,
      ...ueberschreibe,
    };
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await kanalSeiteApi.save(slug, rumpf);
      await laden();
      setNotice(ueberschreibe?.aktiv === true
        ? "Die Hörerseite des Kanals ist eingeschaltet. Jetzt in „Seiten bauen“ eine Vorlage wählen."
        : ueberschreibe?.aktiv === false
          ? "Die Hörerseite des Kanals ist ausgeschaltet. Die Seiten bleiben erhalten."
          : "Gespeichert. Die Hörerseite übernimmt es sofort.");
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!geladen || !kanal) {
    return (
      <Page className="max-w-6xl">
        {geladen && error ? <Callout tone="danger">{error}</Callout> : (
          <div className="space-y-4" aria-label="Wird geladen …"><Skeleton className="h-16 w-72" /><Skeleton className="h-72" /></div>
        )}
      </Page>
    );
  }

  const veroeffentlicht = seiten.filter((p) => p.published_at).length;

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow={<Link to="/channels" className="hover:text-brand-primary">Kanäle</Link>}
        title={kanal.name}
        description={<span className="font-mono">/{kanal.slug}</span>}
      >
        <KanalReiter slug={kanal.slug} aktiv="/hoererseite" />
      </PageHeader>

      <div className="space-y-5">
        {/* Schalter und Zustand */}
        <section className={`card p-5 ${form.aktiv ? "brand-glow" : ""}`}>
          <div className="flex items-start gap-4">
            <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${form.aktiv ? "bg-brand-primary/10 text-brand-primary" : "bg-input text-muted"}`}>
              <Sym name="layout" className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-semibold text-primary">Eigene Hörerseite</h2>
                <Schalter
                  checked={form.aktiv}
                  disabled={!darfVerwalten || busy}
                  label={form.aktiv ? "Eigene Hörerseite ausschalten" : "Eigene Hörerseite einschalten"}
                  onChange={(an) => {
                    if (!an && !window.confirm("Hörerseite des Kanals ausschalten? Die Seiten bleiben gespeichert, sind aber für Hörer nicht mehr erreichbar.")) return;
                    void speichern(undefined, { aktiv: an });
                  }}
                />
              </div>
              <p className="mt-1 text-sm text-muted">
                {form.aktiv
                  ? <>„{kanal.name}“ hat eine eigene Seite mit eigenen Unterseiten. Player und „Jetzt läuft“ zeigen dort diesen Kanal.</>
                  : <>Aus: Hörer finden „{kanal.name}“ auf der Seite des Radios. Eingeschaltet bekommt der Kanal eine eigene Seite mit denselben Bausteinen und Vorlagen – die Radioseite bleibt, wie sie ist.</>}
              </p>
              {!darfVerwalten && <p className="mt-1 text-xs text-muted">Ein- und ausschalten darf die Radioleitung oder wer den Kanal verwaltet.</p>}

              {form.aktiv && (
                <div className="mt-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge tone={veroeffentlicht > 0 ? "success" : "warning"} dot>
                      {seiten.length === 0 ? "noch keine Seiten" : `${seiten.length} ${seiten.length === 1 ? "Seite" : "Seiten"}, ${veroeffentlicht} veröffentlicht`}
                    </Badge>
                    {seiten.length === 0 && <span className="text-xs text-muted">Bis zur ersten Seite zeigt die Adresse eine schlichte Startseite mit dem Player.</span>}
                  </div>
                  <div className="flex min-w-0 items-center gap-2 rounded-lg bg-input px-3 py-2 ring-1 ring-[var(--border)]">
                    <Sym name="link" className="h-4 w-4 flex-shrink-0 text-muted" />
                    <a href={seite} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-mono text-xs text-secondary hover:text-brand-primary hover:underline">{seite}</a>
                    <button type="button" onClick={() => { void navigator.clipboard?.writeText(seite); setNotice("Link kopiert."); }}
                      className="btn btn-ghost btn-sm flex-shrink-0" aria-label="Link kopieren"><Sym name="copy" className="h-3.5 w-3.5" /><span className="hidden sm:inline">Kopieren</span></button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {darfPflegen && (
                      <Link to={`/seiten?kanal=${encodeURIComponent(kanal.slug)}`} className="btn btn-primary"><Sym name="layout" />Seiten bauen</Link>
                    )}
                    <a href={seite} target="_blank" rel="noreferrer" className="btn btn-secondary"><Sym name="external" />Hörerseite öffnen</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {error && <Callout tone="danger">{error}</Callout>}
        {!error && notice && <Meldungen notice={notice} onClear={() => setNotice(null)} />}

        <form onSubmit={(e) => void speichern(e)} className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="min-w-0 space-y-5">
            <fieldset disabled={!darfPflegen} className="min-w-0 space-y-5">
              <Card title="Aussehen" description="Leere Felder übernimmt die Seite vom Radio. So bleibt der Kanal erkennbar Teil des Radios, bis ihr etwas Eigenes eintragt.">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="field-label">Name</span>
                    <input className="input" value={form.titel ?? ""} maxLength={80} placeholder={kanal.name}
                      onChange={(e) => set({ titel: e.target.value })} />
                  </label>
                  <label className="block">
                    <span className="field-label">Untertitel</span>
                    <input className="input" value={form.untertitel ?? ""} maxLength={120} placeholder={radio.claim || "z. B. Rock rund um die Uhr"}
                      onChange={(e) => set({ untertitel: e.target.value })} />
                  </label>
                  <div className="sm:col-span-2">
                    <span className="field-label">Logo</span>
                    <ImageField value={form.logo_url ?? ""} onChange={(v) => set({ logo_url: v })} publicUrl={basis} label="Logo" />
                    <span className="field-hint block">{form.logo_url ? "Aus der Bild-Mediathek des Radios." : "Ohne eigenes Logo erscheint das Logo des Radios."}</span>
                  </div>
                </div>
              </Card>

              <Card
                title="Farben"
                description="Eigene Farben für diesen Kanal. „vom Radio“ heißt: Die Farbe folgt dem Radio, auch wenn es sie später ändert."
              >
                {Object.keys(form.farben).length > 0 && (
                  <div className="-mt-1 mb-4 flex justify-end">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setForm((f) => ({ ...f, farben: {} })); setDirty(true); }}>
                      Alle vom Radio übernehmen
                    </button>
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  {FARBEN.map((f) => (
                    <FarbFeld
                      key={f.key}
                      label={f.label}
                      hint={f.hint}
                      wert={form.farben[f.key] ?? ""}
                      radio={radio.colors?.[f.key] || GRUND[f.key]}
                      onChange={(v) => setFarbe(f.key, v)}
                    />
                  ))}
                </div>
              </Card>

              <Card title="Verbindung zum Radio">
                <label className="flex items-start justify-between gap-4">
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-primary">Link „zurück zum Radio“ zeigen</span>
                    <span className="mt-0.5 block text-xs text-muted">Oben auf der Kanalseite steht dann ein Link zur Hauptseite von „{radio.name || user?.tenant_name || "eurem Radio"}“.</span>
                  </span>
                  <Schalter checked={form.zurueck_link} disabled={!darfPflegen} label="Link zurück zum Radio" onChange={(v) => set({ zurueck_link: v })} />
                </label>
              </Card>
            </fieldset>

            <Card title="Eigene Adresse" description="Optional: Die Kanalseite zusätzlich unter einer eigenen Domain, z. B. rock.beispiel.de.">
              <label className="block">
                <span className="field-label">Adresse (ohne https://)</span>
                <span className="relative block">
                  <Sym name="link" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                  <input className="input pl-9 font-mono text-sm" value={adresse} disabled={!darfVerwalten} placeholder="rock.beispiel.de"
                    onChange={(e) => { setAdresse(e.target.value); setDirty(true); }}
                    onBlur={() => setAdresse((a) => hostAus(a))}
                    inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false} />
                </span>
                {!darfVerwalten && <span className="field-hint block">Die Adresse ändert die Radioleitung oder wer den Kanal verwaltet.</span>}
              </label>
              <div className="well mt-4 text-sm text-secondary">
                <div className="font-medium text-primary">Damit die Adresse funktioniert, braucht es zwei Schritte außerhalb der Suite:</div>
                <ol className="mt-2 list-decimal space-y-1.5 pl-5">
                  <li>
                    <strong className="font-medium text-primary">DNS beim Domain-Anbieter:</strong> einen Eintrag für die Adresse anlegen, der auf den Server der Hörerseite zeigt –
                    am einfachsten ein CNAME auf <code className="break-all font-mono text-xs">{hostVon(basis) || "die Adresse der Hörerseite"}</code>.
                  </li>
                  <li>
                    <strong className="font-medium text-primary">Weiterleitung und Zertifikat:</strong> Wer den Server betreut, trägt die Adresse im Reverse-Proxy
                    (z. B. nginx) ein, leitet sie an die Hörerseite weiter und stellt ein HTTPS-Zertifikat aus.
                  </li>
                </ol>
                <p className="mt-2 text-xs text-muted">Bis beides steht, ist die Seite weiter unter der normalen Adresse erreichbar. Nichts geht kaputt, wenn die Adresse noch nicht zeigt.</p>
              </div>
            </Card>

            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" disabled={busy || !dirty || (!darfPflegen && !darfVerwalten)} className="btn btn-primary">
                {busy ? "Speichern …" : "Speichern"}
              </button>
              {dirty && <span className="text-xs text-muted">Noch nicht gespeichert.</span>}
              {!darfPflegen && !darfVerwalten && <span className="text-xs text-muted">Ändern darf, wer im Kanal Seiten pflegt, und die Radioleitung.</span>}
            </div>
          </div>

          <aside className="min-w-0 lg:sticky lg:top-4">
            <div className="section-label mb-2">Vorschau</div>
            <KanalVorschau
              name={form.titel?.trim() || kanal.name}
              untertitel={form.untertitel?.trim() || radio.claim || ""}
              logo={form.logo_url ? `${basis}${form.logo_url}` : radio.logo_url || ""}
              farbe={(k) => (form.farben[k] && HEX.test(form.farben[k]!) ? form.farben[k]! : radio.colors?.[k] || GRUND[k])}
              zurueck={form.zurueck_link ? (radio.name || user?.tenant_name || "Radio") : null}
            />
            <p className="mt-2 text-xs text-muted">Nur zur Orientierung – Schrift, Ecken und Dichte kommen aus den Einstellungen des Radios.</p>
          </aside>
        </form>
      </div>
    </Page>
  );
}

/** Hostname ohne Port – ein CNAME zeigt immer auf einen Namen, nie auf einen Port. */
function hostVon(url: string): string {
  try { return new URL(url).hostname; } catch { return ""; }
}

/** Farbfeld mit „vom Radio“: leer = erben, dann zeigt das Feld die Radiofarbe blass an. */
function FarbFeld({ label, hint, wert, radio, onChange }: {
  label: string;
  hint: string;
  wert: string;
  radio: string;
  onChange: (v: string | null) => void;
}): JSX.Element {
  const erbt = wert === "";
  const anzeige = erbt ? radio : wert;
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="field-label">{label}</span>
        {erbt
          ? <span className="text-[0.7rem] text-muted">vom Radio</span>
          : <button type="button" onClick={() => onChange(null)} className="text-[0.7rem] text-muted underline-offset-2 hover:text-primary hover:underline" style={{ minHeight: 0 }}>vom Radio übernehmen</button>}
      </div>
      <div className="flex items-center gap-2">
        <span className={`relative h-[2.375rem] w-[2.375rem] flex-shrink-0 overflow-hidden rounded-lg border border-default ${erbt ? "opacity-60" : ""}`} style={{ background: HEX.test(anzeige) ? anzeige : radio }}>
          <input
            type="color"
            value={HEX.test(anzeige) ? anzeige : "#000000"}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label={`${label} wählen`}
          />
        </span>
        <input
          type="text"
          className="input min-w-0 font-mono text-xs uppercase"
          value={wert}
          placeholder={radio.toUpperCase()}
          aria-label={label}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      <span className="field-hint block">{hint}</span>
    </div>
  );
}

/** Miniatur der Kanalseite, angelehnt an die Vorschau bei „Hörerseite & Farben“. */
function KanalVorschau({ name, untertitel, logo, farbe, zurueck }: {
  name: string;
  untertitel: string;
  logo: string;
  farbe: (k: FarbSchluessel) => string;
  zurueck: string | null;
}): JSX.Element {
  const primary = farbe("primary");
  const secondary = farbe("secondary");
  const accent = farbe("accent");
  const background = farbe("background");
  const text = farbe("text");
  return (
    <div className="card overflow-hidden">
      {zurueck && (
        <div className="truncate px-4 py-1.5 text-[0.7rem] font-medium" style={{ background: `${secondary}ee`, color: "#ffffffcc" }}>
          ← zurück zu {zurueck}
        </div>
      )}
      <div className="px-4 pb-5 pt-4 text-white" style={{ background: `linear-gradient(135deg, ${secondary}, ${secondary}dd)` }}>
        <div className="flex items-center gap-3">
          {logo
            ? <img src={logo} alt="" className="h-10 w-10 rounded-lg bg-white/10 object-contain" />
            : <span className="flex h-10 w-10 items-center justify-center rounded-lg text-sm font-bold" style={{ background: primary }}>{name.slice(0, 2).toUpperCase()}</span>}
          <div className="min-w-0">
            <div className="truncate font-semibold">{name}</div>
            {untertitel && <div className="truncate text-xs opacity-75">{untertitel}</div>}
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full shadow-lg" style={{ background: primary }}>
            <Sym name="play" className="ml-0.5 h-4 w-4 fill-current" />
          </span>
          <div className="min-w-0 text-xs">
            <div className="flex items-center gap-1.5 font-semibold uppercase tracking-wider" style={{ color: accent }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />Live
            </div>
            <div className="truncate opacity-90">Jetzt läuft auf {name}</div>
          </div>
        </div>
      </div>
      <div className="space-y-3 p-4" style={{ background, color: text }}>
        <p className="text-xs leading-relaxed opacity-80">Willkommen auf der Seite von {name}.</p>
        <div className="flex gap-2">
          <span className="rounded-md px-3 py-1.5 text-xs font-medium text-white" style={{ background: primary }}>Wunsch senden</span>
          <span className="rounded-md border px-3 py-1.5 text-xs font-medium" style={{ borderColor: primary, color: primary }}>Programm</span>
        </div>
        <div className="flex gap-1.5 pt-1">
          {[primary, secondary, accent, background, text].map((c, i) => (
            <span key={i} className="h-5 flex-1 rounded" style={{ background: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.12)" }} title={c} />
          ))}
        </div>
      </div>
    </div>
  );
}
